import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type { Plugin } from './plugin'

// TODO: implement needed selectors from below
export class Selectors {
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly report

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly label
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly name
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly code
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly pageTitle

  // NOTE: intentionally not typed here so that type can be inferred
  public readonly content
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly columns
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly numberOfColumns
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly rows
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly headerRow
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly bodyRows
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly grandTotalRow

  /**
   * Returns `true` if some of the cells in the first row of the {@link content}
   * have style set to {@link ents.extra.common.Report.Cell.Style.ColumnHeader}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly firstRowInContentIsHeaderRow
  /**
   * Returns `true` if some of the cells in the last row of the {@link content}
   * have style set to {@link ents.extra.common.Report.Cell.Style.GrandTotal}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly lastRowInContentIsGrandTotalRow

  constructor(plugin: Plugin) {
    this.report =
      plugin.collection.plugins.currentEntityPlugin.feature.selectors.getEntity()
    this.label = this.report.project(report => report?.label)
    this.name = this.label.project(label => label?.name)
    this.code = this.label.project(label => label?.code)
    this.pageTitle = this.name.project(this.code.asSelector, (name, code) =>
      [code, name].filter(TypeChecker.isNotNullish.raw).join(' - '),
    )

    this.content = this.report.project(report => report?.content)
    this.rows = this.content.project(content => content?.rows)
    this.columns = this.content.project(content => content?.columns)

    this.firstRowInContentIsHeaderRow = this.rows.project(
      rows =>
        !!rows?.[0]?.cells.some(
          cell =>
            cell?.style === ents.extra.common.Report.Cell.Style.ColumnHeader,
        ),
    )
    this.lastRowInContentIsGrandTotalRow = this.rows.project(
      rows =>
        !!rows?.[rows.length - 1]?.cells.some(
          cell =>
            cell?.style === ents.extra.common.Report.Cell.Style.GrandTotal,
        ),
    )

    this.numberOfColumns = this.columns.project(
      this.rows.asSelector,
      (columns, rows) =>
        // If the `columns` array is defined in the report content, use its length to
        // determine the number of columns.
        // If it is not defined, iterate over rows and calculate the number of
        // cells for each row. Use the row with the highest number of cells as the
        // number of columns
        columns?.length ??
        Math.max(...(rows?.map(row => _calculateTotalSpanOfRow(row)) ?? [0])),
    )

    this.headerRow = this.rows.project(
      this.firstRowInContentIsHeaderRow.asSelector,
      this.numberOfColumns.asSelector,
      (rows, firstRowInContentIsHeaderRow, numberOfColumns) => {
        // If first row from content is not defined as report header, take total
        // number of columns and generate default cells which will represent report
        // headers
        if (!firstRowInContentIsHeaderRow) {
          return _blankRow(
            numberOfColumns,
            ents.extra.common.Report.Cell.Style.ColumnHeader,
          )
        }
        let headerRow = rows?.[0]
        if (!headerRow) {
          return undefined
        }
        const numberOfHeaderCells = _calculateTotalSpanOfRow(headerRow)
        // If number of header cells is less then total number of columns, we need
        // to add additional cells to header row so that appropriate number of
        // columns can be rendered based on header row
        if (numberOfHeaderCells < numberOfColumns) {
          headerRow = {
            ...headerRow,
            cells: [
              ...headerRow.cells,
              ..._blankRow(
                numberOfColumns - numberOfHeaderCells,
                ents.extra.common.Report.Cell.Style.ColumnHeader,
              ).cells,
            ],
          }
        }
        return _standardizeRow(
          headerRow,
          ents.extra.common.Report.Cell.Style.ColumnHeader,
        )
      },
    )

    this.grandTotalRow = this.rows.project(
      this.lastRowInContentIsGrandTotalRow.asSelector,
      (rows, lastRowInContentIsGrandTotalRow) => {
        if (rows?.length && lastRowInContentIsGrandTotalRow) {
          return _standardizeRow(
            rows[rows.length - 1],
            ents.extra.common.Report.Cell.Style.GrandTotal,
          )
        } else {
          return undefined
        }
      },
    )

    this.bodyRows = this.rows.project(
      this.firstRowInContentIsHeaderRow.asSelector,
      this.lastRowInContentIsGrandTotalRow.asSelector,
      this.numberOfColumns.asSelector,
      (
        rows,
        firstRowInContentIsHeaderRow,
        lastRowInContentIsGrandTotalRow,
        numberOfColumns,
      ) =>
        rows
          ?.slice(
            firstRowInContentIsHeaderRow ? 1 : 0,
            rows?.length - (lastRowInContentIsGrandTotalRow ? 1 : 0),
          )
          ?.map(row => _standardizeRow(row) ?? _blankRow(numberOfColumns)),
    )
  }
}

/**
 * Ensures the given {@link ents.extra.common.Report.Row} matches the expected
 * structure for the UI
 */
function _standardizeRow(
  row?: ents.extra.common.Report.Row,
  defaultStyle?: ents.extra.common.Report.Cell.Style,
): ents.extra.common.Report.Row | undefined {
  return (
    row && {
      cells: row.cells.flatMap(cell => {
        // if cell is not defined fill it with default styles and content
        if (!cell) {
          return _blankCell(defaultStyle)
        }
        const style = cell.style ?? defaultStyle
        // if cell needs to span multiple columns, create new cells based on
        // columnSpan number
        if (cell.columnSpan) {
          return Array.from({ length: cell.columnSpan }, (emptyCell, index) =>
            // Fill all cells with defined style.
            // Fill just the first cell with defined cell properties and
            // other cells with default content
            index === 0 ? { ...cell, style } : _blankCell(style),
          )
        }
        // if cell is defined and there is no columnSpan, return the cell with defined style
        return { ...cell, style: style }
      }),
    }
  )
}

function _blankRow(
  length: number,
  style?: ents.extra.common.Report.Cell.Style,
): ents.extra.common.Report.Row {
  return {
    cells: Array.from({ length }, () => _blankCell(style)),
  }
}

function _blankCell(
  style?: ents.extra.common.Report.Cell.Style,
): ents.extra.common.Report.Cell {
  return {
    content: {
      raw: '',
      type: ents.core.common.value.types.Intrinsic.Text,
    },
    style,
  }
}

/**
 * Calculate the number of cells for a given row based on the `cells` array
 * and the `columnSpan` property, if present on cell.
 */
function _calculateTotalSpanOfRow(
  row: ents.extra.common.Report.Row | undefined,
): number {
  return (
    row?.cells.reduce(
      (total, { columnSpan = 1 } = {}) => total + columnSpan,
      0,
    ) ?? 0
  )
}
